import { Button } from "react-bootstrap";
import React, { useState } from "react";
import "./importParts.scss";
import { ReactComponent as BlackImportIcon } from "../../assets/images/blackImportIcon.svg";
import { ReactComponent as PartThumbnail } from "../../assets/images/part-thumbnail.svg";
import { uploadPart } from "../../services/part/partService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/store";
import {
  showErrorToast,
  showSuccessToast,
} from "../../utils/toast/notificationPopup";
import { showLoader } from "../../redux/features/loaderSlice";

interface filesToBeImported {
  progress: number;
  error: string;
  status: string;
  file: any;
  name: string;
}

const ImportParts = (props) => {
  const { onSelectPart } = props;
  const [filesToBeImported, setFilesToBeImported] = useState<
    filesToBeImported[]
  >([]);
  const [isImportSubmitted, setIsImportSubmitted] = useState(false);
  const [partName, setPartName] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const userData = useSelector((state: RootState) => state.login);
  const [isDragging, setIsDragging] = useState(false);
  const { userId } = userData;

  const isValidFileType = (file: File): boolean => {
    const lowerName = file.name.toLowerCase();
    return (
      lowerName.endsWith(".stl") ||
      lowerName.endsWith(".step") ||
      lowerName.endsWith(".stp")
    );
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(isValidFileType);

    if (validFiles.length !== files.length) {
      showErrorToast("Unsupported file type. Please upload an STL, STEP or STP file.");
    }

    if (validFiles.length > 0) {
      const syntheticEvent = {
        target: {
          files: validFiles,
          value: "",
        },
      };
      onAddFilesForImport(syntheticEvent);
    }
  };

  const onAddFilesForImport = (e: any) => {

    const files = Array.from(
      e.target.files
    ) as File[];
  
    const invalidFiles = files.filter(
      (file) => !isValidFileType(file)
    );
  
    if (invalidFiles.length > 0) {
  
      showErrorToast(
        "Unsupported file type. Please upload an STL, STEP or STP file."
      );
  
      e.target.value = "";
  
      return;
    }
  
    files.forEach((file: any) => {
  
      if (
        !filesToBeImported.some(
          (el) => el.file.name === file.name
        )
      ) {
  
        setFilesToBeImported(
          (prev) => [
            ...prev,
            {
              progress: 0,
              error: "",
              status: "",
              file,
              name: file.name
            }
          ]
        );
      }
    });
  
    e.target.value = "";
  };

  const onDeleteFilesForImport = (fileName) =>
    setFilesToBeImported((filesToBeImported) =>
      filesToBeImported.filter((e: any) => e.file.name !== fileName)
    );

  const handleImportPart = async () => {
    setIsImportSubmitted(true);
    
    if (!filesToBeImported.length) {
      showErrorToast("Please select a file to upload");
      return;
    }
    
    if (!partName) {
      showErrorToast("Please enter a part name");
      return;
    }
    
    if (filesToBeImported.length && partName) {
      const checkFileExt = filesToBeImported[0].file.name.split(".");
      const fileExt = checkFileExt[checkFileExt.length - 1];
      dispatch(showLoader({ loaderVisibleState: true }));
      await uploadPart(
        partName,
        partName + "." + fileExt,
        filesToBeImported[0].file,
        userId
      )
        .then(async (response) => {
          if (response?.data?.success) {
            dispatch(showLoader({ loaderVisibleState: false }));
            showSuccessToast("Part Imported Successfully");
            
            const ext = fileExt.toLowerCase();

            const hasSupportedFile =
              ext === "stl" ||
              ext === "step" ||
              ext === "stp";
            if (hasSupportedFile) {
              onSelectPart(
                {
                  PartName: partName,
                  modelName: partName + "." + fileExt,
                  PartID: response?.data?.PartID,
                },
                ""
              );
            }
            
            // Reset form
            setFilesToBeImported([]);
            setPartName("");
            setIsImportSubmitted(false);
          }
        })
        .catch((error) => {

          dispatch(
            showLoader({
              loaderVisibleState: false
            })
          );
        
          const errorMessage =
            error?.response?.data?.message ||
            "Upload failed";
        
          showErrorToast(errorMessage);
        });
    }
  };

  const handlePartNameChange = (e) => {
    setPartName(e.target.value);
  };

  return (
    <div className="upload-design-container">
      <div className="upload-design-content">
        <div
          className={`drop-zone mb-3 ${isDragging ? "dragging" : ""} ${isImportSubmitted && !filesToBeImported.length ? "error-border" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            className="file-input-box"
            onChange={(event) => onAddFilesForImport(event)}
            multiple={false}
            accept=".stl,.STL,.step,.STEP,.stp,.STP"
          />
          <BlackImportIcon />
          <p>
            Drag & Drop or <span>Choose file</span> to upload
          </p>
          <p className="support-text">Supported formats STL, STEP, STP</p>
        </div>
        {isImportSubmitted && !filesToBeImported.length && (
          <span className="part-error-text" style={{ marginTop: '-10px', display: 'block', marginBottom: '10px' }}>
            Please select a file*
          </span>
        )}

        <div className="part-input">
          <label className="part-label">*Part name:</label>
          <div className="input-container">
            <input
              type="text"
              className={
                isImportSubmitted && !partName
                  ? "part-input-box error"
                  : "part-input-box"
              }
              value={partName}
              onChange={handlePartNameChange}
            />
            {isImportSubmitted && !partName ? (
              <span className="part-error-text">Part name is required*</span>
            ) : (
              ""
            )}
          </div>
        </div>

        <div className="added-files-list">
          {filesToBeImported.length !== 0 && (
            <ul>
              {[...filesToBeImported].map((file: any, idx) => (
                <li key={idx}>
                  <div className="d-flex">
                    <div className="part-thumbnail">
                      <PartThumbnail />
                    </div>
                    <input
                      type="text"
                      disabled
                      value={file.name}
                    ></input>
                    <span
                      onClick={() => onDeleteFilesForImport(file.file.name)}
                      className="remove-import"
                    ></span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="upload-button-container">
          <Button
            variant="primary"
            className="btn-primary"
            onClick={handleImportPart}
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ImportParts);
